import { defineStore, storeToRefs } from "pinia";
import { computed, ref } from "vue";
import { useRoute } from "vue-router";
import useUserStore from "@/stores/user";

export default defineStore("domain", () => {
  const route = useRoute();
  const userStore = useUserStore();
  const { vendorKey } = storeToRefs(userStore);

  const domain = ref(null);
  const overrideHistory = ref(null);
  const activeDco = ref(null);
  const suggestions = ref(null);
  const domainExists = computed(
    () => domain.value != null && domain.value.name === route.params.domain,
  );
  const loadingDomain = ref(false);
  const loadingOverrides = ref(false);

  async function getDomain(d, vendorKeyOverride = null) {
    loadingDomain.value = true;
    const vendor = vendorKeyOverride ? vendorKeyOverride : vendorKey.value;
    if (vendor)
      try {
        const response = await fetch(`/api/cmp/${vendor}/domains/${d}/`);

        if (!response.ok) {
          throw new Error("Failed to get domain data");
        }

        domain.value = await response.json();
      } finally {
        loadingDomain.value = false;
      }
  }

  async function ensureDomain(domain) {
    if (domainExists.value) {
      return;
    }

    await getDomain(domain);
  }

  async function getOverrideHistory(domainName, overrideType, subkey = null) {
    overrideHistory.value = null;
    const params = [
      ["override_type", overrideType],
      ["suggestions_only", true],
      ["order_by", "-last_reviewed_or_created"],
      ["count", 10],
      ["status", "approved"],
      ["status", "denied"],
      ["status", "retired"],
    ];
    if (subkey) {
      params.push(["subkey", subkey]);
    }
    const paramString = new URLSearchParams(params).toString();
    const url = `/api/domains/${domainName}/overrides/?${paramString}`;
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error("Failed to get suggestion history data");
    }

    overrideHistory.value = await response.json();
  }

  async function getActiveOverride(domainName, overrideType, subkey = null) {
    activeDco.value = null;
    const params = {
      override_type: overrideType,
      status: "approved",
      count: 1,
    };
    if (subkey) {
      params.subkey = subkey;
    }
    const paramString = new URLSearchParams(params).toString();
    const url = `/api/domains/${domainName}/overrides/?${paramString}`;
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to get active DCO for ${domainName}`);
    }

    const data = await response.json();
    if (data.length) {
      activeDco.value = data[0];
    }
  }

  async function getSuggestions(domainName, overrideType, subkey = null) {
    suggestions.value = null;
    const params = {
      override_type: overrideType,
      status: "pending",
      suggestions_only: true,
    };
    if (subkey) {
      params.subkey = subkey;
    }
    const paramString = new URLSearchParams(params).toString();
    const url = `/api/domains/${domainName}/overrides/?${paramString}`;
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to get suggestions for ${domainName}`);
    }

    suggestions.value = await response.json();
  }

  async function getOverrides(domainName, overrideType, subkey = null) {
    loadingOverrides.value = true;
    await Promise.all([
      getOverrideHistory(domainName, overrideType, subkey),
      getActiveOverride(domainName, overrideType, subkey),
      getSuggestions(domainName, overrideType, subkey),
    ]);
    loadingOverrides.value = false;
  }

  function clearData() {
    domain.value = null;
    overrideHistory.value = null;
    activeDco.value = null;
    suggestions.value = null;
  }

  return {
    domain,
    overrideHistory,
    activeDco,
    suggestions,
    domainExists,
    loadingDomain,
    loadingOverrides,
    getDomain,
    ensureDomain,
    getOverrides,
    clearData,
  };
});
